/**
 * Tab halaman Laporan — MURNI, tanpa React.
 *
 * ADA KARENA LAPORAN TERSEBAR. Sebelum ini, Workforce punya empat layar laporan yang berdiri
 * sendiri-sendiri di menu (Pantauan Absensi, Rekap Bulanan, Pantauan Tugas, Pengiriman). Owner
 * membandingkannya dengan Reports di CRM — satu pintu, isinya tab — dan memintanya disamakan:
 * "semua terlihat dalam satu fitur". Menu yang berisi empat kata yang mirip memaksa orang menebak
 * mana yang memuat angka yang dicarinya.
 *
 * Aturan izinnya MENGIKUTI `navigation.ts` persis, termasuk `workforce.superuser` sebagai pembuka
 * segalanya. Menyalin aturannya dengan sedikit berbeda akan menghasilkan tab yang dijanjikan lalu
 * ditolak API — persis yang menimpa EMPLOYEE dan COURIER sebelum 23 Agu 2026 pada menu Pantauan
 * Absensi, dan alasan `allOf` ada sejak itu.
 */

export interface TabLaporan {
  readonly key: string;
  readonly label: string;
  /** Salah satu saja cukup. Kosong = selalu tampil. */
  readonly anyOf: readonly string[];
  /** SELURUHNYA wajib. Kosong = tidak membatasi. */
  readonly allOf?: readonly string[];
}

/**
 * Urutannya disengaja: Ringkasan lebih dulu karena ia menjawab "bagaimana keadaannya" tanpa
 * memaksa memilih apa pun. Sisanya dari yang paling sering dibuka.
 */
export const TAB_LAPORAN: readonly TabLaporan[] = [
  {
    key: 'ringkasan',
    label: 'Ringkasan',
    /*
     * Ringkasan menampilkan apa yang boleh dilihat pemanggilnya, dan menyembunyikan sisanya —
     * jadi cukup salah satu izin baca. Menuntut keduanya akan menyembunyikan seluruh halaman
     * Laporan dari orang yang sebenarnya berhak melihat separuhnya.
     */
    anyOf: ['workforce.attendance.read', 'workforce.task.read'],
  },
  {
    key: 'absensi',
    label: 'Absensi',
    // Layar ini memanggil EMPAT endpoint dengan dua izin berbeda — lihat `navigation.ts`.
    anyOf: ['workforce.attendance.read'],
    allOf: ['workforce.attendance.read', 'workforce.worker.read'],
  },
  {
    key: 'bulanan',
    label: 'Rekap Bulanan',
    // Endpoint yang sama persis dengan tab Absensi, hanya rentangnya sebulan.
    anyOf: ['workforce.attendance.read'],
    allOf: ['workforce.attendance.read', 'workforce.worker.read'],
  },
  {
    key: 'tugas',
    label: 'Tugas',
    anyOf: ['workforce.task.read'],
  },
  {
    key: 'pengiriman',
    label: 'Pengiriman',
    anyOf: ['workforce.delivery.read'],
  },
];

/** Tab yang boleh dilihat pemegang izin ini. Aturannya identik dengan `visibleNavItems`. */
export function tabTerlihat(
  permissions: readonly string[],
  tabs: readonly TabLaporan[] = TAB_LAPORAN,
): readonly TabLaporan[] {
  const dimiliki = new Set(permissions);
  const superuser = dimiliki.has('workforce.superuser');
  return tabs.filter((tab) => {
    if (superuser) return true;
    const lolosAny = tab.anyOf.length === 0 || tab.anyOf.some((p) => dimiliki.has(p));
    const lolosAll = (tab.allOf ?? []).every((p) => dimiliki.has(p));
    return lolosAny && lolosAll;
  });
}

/**
 * Tab yang harus dibuka: yang DIMINTA bila boleh dilihat, kalau tidak yang pertama yang boleh.
 *
 * Mengembalikan `null` hanya bila tidak ada satu pun tab yang boleh — dan itu bukan keadaan
 * mustahil: seseorang dapat membuka tautan lama sesudah perannya dicabut. Halaman yang memilih
 * tab pertama secara membabi buta akan memanggil endpoint yang lalu menolaknya, dan yang terlihat
 * adalah galat teknis, bukan "Anda tidak berhak".
 *
 * Tab yang diminta tetapi TIDAK boleh dilihat sengaja jatuh ke tab pertama, bukan menjadi galat:
 * tautan yang dibagikan antar-peran adalah hal biasa, dan orang yang menerimanya lebih terbantu
 * melihat laporan yang boleh ia lihat daripada layar penolakan.
 */
export function tabAwal(
  permissions: readonly string[],
  diminta?: string | null,
  tabs: readonly TabLaporan[] = TAB_LAPORAN,
): string | null {
  const terlihat = tabTerlihat(permissions, tabs);
  if (terlihat.length === 0) return null;
  const cocok = terlihat.find((t) => t.key === diminta);
  return cocok ? cocok.key : (terlihat[0] as TabLaporan).key;
}
